/**
 * IPI Name Number: 11 dígitos, los dos últimos de control (módulo 101).
 *
 * El CAE viejo (9 dígitos) es el mismo número sin los dos ceros del principio,
 * y SADAIC muestra los números sin esos ceros (Cerati: 260876058). Por eso de 1
 * a 11 dígitos se acepta y se rellena con ceros a la izquierda: 00260876058.
 * No se supone que empiece con 00: existen los 01… (Sony Music Publishing
 * Argentina, 01083029966).
 *
 * Control, regla de SUISA: S = d1·10 + d2·9 + … + d9·2, r = S mod 101 y
 * control = r ≤ 1 ? 00 : 101 − r. La regla ingenua (S + control) mod 101 = 0
 * rechaza los IPI reales con r = 1.
 *
 * ESPEJO byte-idéntico: jupiter-server/src/common/validation/identifiers/ipi.ts
 * y jupiter-client/src/entities/common/validation/identifiers/ipi.ts
 */

import type { IdentifierCheck } from "./index";

/** Puntos, espacios (con NBSP), guiones Unicode y caracteres de ancho cero. */
const SEPARATORS = /[.\s\u200B-\u200D\u2060\uFEFF\u2010-\u2015\u2212-]/g;

function upperAsciiLetters(text: string): string {
  return text.replace(/[a-z]/g, (c) => c.toUpperCase());
}

function controlDigitsOf(nineDigits: string): string {
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += (nineDigits.charCodeAt(i) - 48) * (10 - i);
  const r = sum % 101;
  return String(r <= 1 ? 0 : 101 - r).padStart(2, "0");
}

export function checkIpi(raw: string | null | undefined): IdentifierCheck {
  if (raw === null || raw === undefined) return { value: null };
  const trimmed = String(raw).normalize("NFKC").trim();
  if (trimmed === "") return { value: null };

  const compact = upperAsciiLetters(trimmed).replace(SEPARATORS, "");

  if (/^I[0-9]{10}$/.test(compact)) {
    return {
      value: compact,
      error: {
        code: "IPI_BASE_NUMBER",
        message:
          "Es un IPI Base Number (I-NNNNNNNNN-C), que identifica a la persona. Acá va el IPI Name Number: 11 dígitos.",
      },
    };
  }
  if (!/^[0-9]+$/.test(compact)) {
    // Si no queda nada al normalizar, el valor es lo que se escribió: nunca "" con error.
    return {
      value: compact || trimmed,
      error: {
        code: "IPI_FORMAT",
        message: "El IPI lleva solo números: los 11 dígitos, o los 9 del CAE.",
      },
    };
  }
  if (compact.length > 11) {
    return {
      value: compact,
      error: {
        code: "IPI_TOO_LONG",
        message: `Tiene ${compact.length} dígitos y el IPI lleva 11: ¿es un ISNI?`,
      },
    };
  }

  const value = compact.padStart(11, "0");
  if (value === "00000000000") {
    return {
      value,
      error: { code: "IPI_ALL_ZEROS", message: "El IPI no puede ser todo ceros." },
    };
  }
  if (controlDigitsOf(value.slice(0, 9)) !== value.slice(9)) {
    return {
      value,
      error: {
        code: "IPI_CHECK_DIGITS",
        message:
          "Los dígitos de control no coinciden. Revisá el número contra la fuente (SADAIC o CISAC).",
      },
    };
  }
  return { value };
}
